import { Box, styled, Tooltip } from "@mui/material";
import { ColumnsPanelTrigger, DataGridPro, DataGridProProps, GridSlots, PropsFromSlot, Toolbar, ToolbarButton } from "@mui/x-data-grid-pro";
import { useContext, useMemo } from "react";
import { AppContext } from "../../../../../../common/contexts/app_context/app_context";
import { useColumnSelectionConfig } from "../../../../../../common/hooks/use_column_selection_config";

import ViewColumnIcon from '@mui/icons-material/ViewColumn';

/**
 * CustomToolbar
 */

const CustomToolbar = ({ toolbarRightSlot }: any) => {
  return (
    <Toolbar>
      <Tooltip title="Colunas">
        <ColumnsPanelTrigger render={<ToolbarButton />}>
          <ViewColumnIcon fontSize="small" />
        </ColumnsPanelTrigger>
      </Tooltip>
    </Toolbar>
  );
}

/**
 * CustomFooter
 */

const parseStat = (v: any): string => {
  if (typeof v === 'number') {
    if (v === 0) return '0,00';
    return `${v.toFixed(2)}`.replaceAll('.', ',');
  }
  return '0,00'
}

export const CustomFooter = ({ mediaGeral }: PropsFromSlot<GridSlots['footer']>) => {
  return (
    <>
      {mediaGeral !== null &&
        <div className="w-full flex flex-col justify-center items-end border-t border-[#90C090] dark:border-[#707070]">
          <div className="flex flex-row me-[2rem]">
            <p className="flex flex-row justify-center items-center w-[25rem] py-4 text-lg font-bold text-white">
              MÉDIA GERAL: <span className="ps-2">{parseStat(mediaGeral)}</span>
            </p>
          </div>
        </div>
      }
    </>
  );
}

/**
 * StyledDataGrid
 */

interface Props extends DataGridProProps {
  tableName: string;
  autoHeight?: boolean;
  minHeight?: string;
  height?: string;
  maxHeight?: string;
  mediaGeral: number | null;
}

export const CustomStyledDataGrid = ({
  tableName,
  autoHeight,
  minHeight,
  maxHeight,
  mediaGeral,
  height = "60vh",
  ...props
}: Props) => {
  const [
    columnsVisibilityModel,
    handleColumnsVisibilityModel,
  ] = useColumnSelectionConfig({
    tabela: tableName,
  });

  const mode = useContext(AppContext).theme
  const StyledDataGrid = useMemo(() => {
    return styled(DataGridPro)(({ theme }) => ({
      '& .MuiDataGrid-columnHeader': {
        backgroundColor: '#aeeece',
      },
      '& .MuiDataGrid-columnSeparator': {
        color: 'black',
      },
      '& .MuiDataGrid-footerContainer': {
        display: 'none',
      },
      '& .MuiDataGrid-toolbar .MuiIconButton-root': {
        color: mode === 'light' ? 'black' : 'white',
      },
      '& .MuiDataGrid-columnHeaderTitleContainerContent': {
        '& .MuiCheckbox-root': {
          color: 'black',
        },
      },
      '& .MuiIconButton-root': {
        color: 'black',
      },
      '& .MuiDataGrid-columnHeaderTitle': {
        wordBreak: 'normal',
        whiteSpace: 'normal',
        lineHeight: 1.5,
        color: 'black',
      },
      backgroundColor: mode === "dark" ? "#2C2C2C" : "#FFFFFF",
    }))
  }, [mode]);

  return (
    <Box sx={{ display: "flex", height: autoHeight ? "auto" : height, width: "100%", minHeight: minHeight, maxHeight: maxHeight }}>
      <StyledDataGrid
        showToolbar={true}
        slots={{
          toolbar: CustomToolbar,
          footer: CustomFooter,
        }}
        slotProps={{
          columnsPanel: {
            style: {
              color: mode === "dark" ? '#ffffff' : "#000000",
            }
          },
          footer: {
            mediaGeral: mediaGeral,
          } as any,
        }}
        columnVisibilityModel={columnsVisibilityModel}
        disableColumnSorting={true}
        multipleColumnsSortingMode='always'
        onColumnVisibilityModelChange={handleColumnsVisibilityModel}
        disableRowSelectionExcludeModel={true}
        {...props}
        disableColumnMenu={false}
        localeText={{
          noRowsLabel: "Não existem itens para mostrar!",

          columnsManagementSearchTitle: 'Buscar',
          columnsManagementNoColumns: 'Não há colunas',
          columnsManagementShowHideAllText: 'Exibir/Esconder',
          columnsManagementReset: 'Reiniciar',
          columnsManagementDeleteIconLabel: 'Limpar',

          pinToLeft: 'Fixar a esquerda',
          pinToRight: 'Fixar a direita',
          unpin: 'Desfixar',

          columnMenuShowColumns: 'Exibir colunas',
          columnMenuManageColumns: 'Gerenciar colunas',

          columnMenuHideColumn: 'Esconder coluna',
          columnMenuUnsort: 'Desordenar',
          columnMenuSortAsc: 'Ordenar ASC',
          columnMenuSortDesc: 'Ordenar DESC',
        }}
      />
    </Box>
  );
}